import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';

const CloudArchitecture = () => {
  const navigate = useNavigate();

  const handleContactClick = () => {
    navigate('/#contact');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Cloud Architecture</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Design and implement scalable, secure, and cost-effective cloud solutions that drive your business forward.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Cloud Architecture Services</h2>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <i className="fas fa-cloud text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Cloud Strategy & Planning</h3>
                  <p className="text-gray-600">Comprehensive cloud adoption strategies tailored to your business needs and goals.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <i className="fas fa-server text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Infrastructure Design</h3>
                  <p className="text-gray-600">Scalable and resilient cloud infrastructure design for optimal performance.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <i className="fas fa-sync text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Migration Services</h3>
                  <p className="text-gray-600">Seamless migration of applications and data to cloud platforms.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Cloud Platforms We Work With</h2>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <i className="fab fa-aws text-4xl text-orange-500 mb-3"></i>
                <h3 className="font-semibold text-gray-900">Amazon Web Services</h3>
                <p className="text-sm text-gray-600">EC2, S3, Lambda, RDS, and more</p>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <i className="fab fa-microsoft text-4xl text-blue-500 mb-3"></i>
                <h3 className="font-semibold text-gray-900">Microsoft Azure</h3>
                <p className="text-sm text-gray-600">Virtual Machines, Storage, Functions</p>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <i className="fab fa-google text-4xl text-green-500 mb-3"></i>
                <h3 className="font-semibold text-gray-900">Google Cloud Platform</h3>
                <p className="text-sm text-gray-600">Compute Engine, Cloud Storage</p>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <i className="fas fa-cloud text-4xl text-purple-500 mb-3"></i>
                <h3 className="font-semibold text-gray-900">Multi-Cloud</h3>
                <p className="text-sm text-gray-600">Hybrid and multi-cloud solutions</p>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Benefits of Our Cloud Architecture</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-chart-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Scalability</h3>
              <p className="text-gray-600">Automatically scale resources up or down based on demand.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-dollar-sign text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Cost Optimization</h3>
              <p className="text-gray-600">Reduce infrastructure costs with optimized cloud resources.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-shield-alt text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Enhanced Security</h3>
              <p className="text-gray-600">Enterprise-grade security with built-in compliance features.</p>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg p-8 text-white">
          <h2 className="text-3xl font-bold mb-8 text-center">Our Cloud Architecture Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">1</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Assessment</h3>
              <p className="text-sm opacity-90">Analyze current infrastructure and requirements</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">2</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Design</h3>
              <p className="text-sm opacity-90">Create optimized cloud architecture blueprint</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">3</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Implementation</h3>
              <p className="text-sm opacity-90">Deploy and configure cloud infrastructure</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">4</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Optimization</h3>
              <p className="text-sm opacity-90">Monitor, optimize, and maintain performance</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Transform Your Infrastructure?</h2>
          <p className="text-xl text-gray-600 mb-8">Let's discuss how our cloud architecture services can benefit your business.</p>
          <button 
            onClick={handleContactClick}
            className="bg-primary hover:bg-blue-700 text-white px-8 py-3 rounded-lg text-lg font-medium transition-colors"
          >
            Get Started Today
          </button>
        </div>
      </div>
    </div>
  );
};

export default CloudArchitecture;
